<script setup>
import { useToast } from 'vue-toastification'
const toast = useToast()
import { inputRequiredRule } from '@/composables/inputRules'
import DateTimePicker from '@/components/DateTimePicker.vue'
import i18n from '../../plugins/i18n'
const { t } = i18n.global
import { computed, onMounted, ref, watch } from 'vue'
import { useIncidentStore } from '@/store/incident'
import CameraPicker from '@/components/formComponents/CameraPicker.vue'
import RegionSpecificationPicker from '@/components/formComponents/RegionSpecificationPicker.vue'
import { DateTime } from 'luxon'
import IncidentOptionalOverrideForm from '@/components/incidentTriggers/IncidentOptionalOverrideForm.vue'
const incidentStore = useIncidentStore()
const props = defineProps({
  incident: { type: Object, default: null },
  showCancelButton: { type: Boolean, default: false },
  shrinkable: { type: Boolean, default: false },
  preFilledFirstTimestamp: { type: Number, default: null },
  preFilledSecondTimestamp: { type: Number, default: null },
  preFilledDisplayIn: { type: Array, default: () => [] },
  preFilledDescription: { type: String, default: null },
})
const emit = defineEmits(['saved', 'cancel'])
const classification = ref(null)
const specification = ref(null)
const isGlobal = ref(false)
const cameras = ref([])
const regionSpecifications = ref([])
const startTimestamp = ref(null)
const endTimestamp = ref(null)
const hasAlreadyEnded = ref(false)
const description = ref(null)
const schema = ref(null)
const loading = ref(false)
const expanded = ref(false)
const form = ref(null)
const optionalOverrides = ref()

const defaultColors = {
  technical: {
    dataLoss: '#808080',
    failureFeedingLine: '#FFFF00',
    leakingFeedingLine: '#FFFF00',
    failureDrinkingLine: '#0000FF',
    leakingDrinkingLine: '#0000FF',
    failureVentilation: '#FF0000',
    partialDefectiveVentilation: '#ADD8E6',
    deviationUnderpressure: '#ADD8E6',
    failureHeating: '#ADD8E6',
    failureSprayCooling: '#0000FF',
    leakingSprayCooling: '#0000FF',
    failureIllumination: '#800080',
  },
  climate: {
    tooCold: '#ADD8E6',
    wayTooCold: '#ADD8E6',
    tooWarm: '#FFA500',
    wayTooWarm: '#FFA500',
    draft: '#ADD8E6',
    airQuality: '#FFA500',
    minAirflowTooLow: '#FFA500',
    airFlowTooHigh: '#ADD8E6',
    airFlowDeviation: '#ADD8E6',
    underpressureTooLow: '#ADD8E6',
    underpressureTooHigh: '#ADD8E6',
  },
  barn: {
    increasedLightIntensity: '#800080',
    decreasedLightIntensity: '#800080',
    abnormalInspection: '#808080',
    raiseElevatedPlatform: '#808080',
    lowerElevatedPlatform: '#808080',
    preparationForCatching: '#808080',
    generalAnomaly: '#808080',
    noise: '#FFC0CB',
    moisture: '#0000FF',
    catching: '#808080',
    animalsMovedIn: '#808080',
    animalsMovedOut: '#808080',
    inspection: '#808080',
    sprayVaccination: '#F5F5DC',
    waterVaccination: '#F5F5DC',
    medication: '#F5F5DC',
    activityDeviation: '#40E0D0',
    repair: '#808080',
    sunOverExposure: '#800080',
  },
  animalDistribution: {
    localDeviation: '#40E0D0',
    drasticallyTooFewAnimals: '#FF0000',
    drasticallyTooManyAnimals: '#FF0000',
  },
  animals: {
    manyDeadAnimals: '#FF0000',
  },
  behaviour: {
    depressionAtDrinkingLine: '#0000FF',
    depressionAtFeedingLine: '#FFFF00',
    drinkingFrequencyIncrease: '#0000FF',
    feedingFrequencyIncrease: '#FFFF00',
    stress: '#FF00FF',
    activityDepression: '#FF00FF',
  },
  disease: {
    coccidiosis: '#800000',
    gumboro: '#800000',
    reovirus: '#800000',
    coliInfection: '#800000',
    enterococci: '#800000',
    clostridia: '#800000',
    streptococci: '#800000',
    respiratoryDisease: '#800000',
    omphalitis: '#800000',
    birdFlu: '#800000',
    deficiencyDiseases: '#800000',
    unspecified: '#800000',
    other: '#800000',
  },
}
const fallbackDefaultColor = '#808080'
const availableClassifications = computed(() => {
  if (schema.value === null) return []
  const classifications = Object.keys(schema.value.barnDocumentation)
  return classifications.map(classification => ({
    value: classification,
    title: t('incidents.barnDocumentation.classifications.' + classification),
  }))
})

const availableSpecifications = computed(() => {
  if (schema.value === null || classification.value === null) return []
  const specifications = Object.keys(schema.value.barnDocumentation[classification.value])
  return specifications.map(specification => ({
    value: specification,
    title: t('incidents.barnDocumentation.specificationTypes.' + specification),
  }))
})

const useTimestamp = computed(() => {
  if (schema.value === null || classification.value === null || specification.value === null) return false
  return schema.value.barnDocumentation[classification.value][specification.value].time === 'timestamp'
})

const allowGlobal = computed(() => {
  if (schema.value === null || classification.value === null || specification.value === null) return false
  return schema.value.barnDocumentation[classification.value][specification.value].location.includes('global')
})

const allowRegions = computed(() => {
  if (schema.value === null || classification.value === null || specification.value === null) return false
  return schema.value.barnDocumentation[classification.value][specification.value].location.includes('regionSpecification')
})

const allowCameraIds = computed(() => {
  if (schema.value === null || classification.value === null || specification.value === null) return false
  return schema.value.barnDocumentation[classification.value][specification.value].location.includes('cameraId')
})

watch(classification, () => {
  specification.value = null
})

watch(specification, () => {
  preFillTimestamps()
  if (!optionalOverrides.value) optionalOverrides.value = {}
  if (!optionalOverrides.value.displayConfiguration) optionalOverrides.value.displayConfiguration = {}
  optionalOverrides.value.displayConfiguration.displaySettings.renderColor =
    defaultColors[classification.value]?.[specification.value] || fallbackDefaultColor
  description.value = props.preFilledDescription || null
  isGlobal.value = false
  cameras.value = []
  regionSpecifications.value = []
})

watch(isGlobal, () => {
  if (isGlobal.value) {
    cameras.value = []
    regionSpecifications.value = []
  }
})

function resetFields() {
  classification.value = null
  specification.value = null
  preFillTimestamps()
  description.value = props.preFilledDescription || null
  isGlobal.value = false
  cameras.value = []
  regionSpecifications.value = []
  hasAlreadyEnded.value = false
  optionalOverrides.value = {}
}

async function saveDocumentation() {
  let incidentId
  let incidentPayload
  form.value.validate()
  if (!form.value.isValid) {
    toast.error(t('toast.fix_errors_above'))
    return
  }
  if (!props.incident) {
    if (!isGlobal.value && !cameras.value.length && !regionSpecifications.value.length) {
      toast.error(t('incidents.barnDocumentation.no_location_selected'))
      return
    }
    if (!startTimestamp.value || (hasAlreadyEnded.value && !endTimestamp.value)) {
      toast.error(t('incidents.barnDocumentation.no_time_selected'))
      return
    }
    if (hasAlreadyEnded.value && endTimestamp.value <= startTimestamp.value) {
      toast.error(t('incidents.barnDocumentation.end_must_be_after_start'))
      return
    }

    incidentPayload = {
      type: 'barnDocumentation',
      trigger: useTimestamp.value ? 'observed' : 'started',
      eventTimestamp: startTimestamp.value.toMillis(),
      identifier: {},
      payload: {
        classification: classification.value,
        specification: {
          type: specification.value,
          location: isGlobal.value
            ? 'global'
            : {
                cameraId: allowCameraIds.value && cameras.value && cameras.value.length > 0 ? cameras.value : undefined,
                regionSpecification:
                  allowRegions.value && regionSpecifications.value && regionSpecifications.value.length > 0 ? regionSpecifications.value : undefined,
              },
        },
        description: description.value ? description.value : undefined,
      },
    }
    if (optionalOverrides.value) {
      incidentPayload.optionalOverrides = optionalOverrides.value
    }
    const incidentData = await incidentStore.triggerGenericIncident(incidentPayload)
    if (!incidentData) {
      toast.error(t('toast.generic_could_not_save'))
      return
    }
    incidentId = incidentData.incident.incidentId
    if (hasAlreadyEnded.value) {
      const successful = await incidentStore.triggerSpecificIncident(incidentId, {
        trigger: 'ended',
        eventTimestamp: endTimestamp.value.toMillis(),
      })
      if (!successful) {
        toast.error(t('toast.generic_could_not_save'))
        return
      }
    }
  } else {
    if (!endTimestamp.value) {
      toast.error(t('incidents.barnDocumentation.no_time_selected'))
      return
    }
    if (endTimestamp.value.toMillis() <= props.incident.firstEventTimestamp) {
      toast.error(t('incidents.barnDocumentation.end_must_be_after_start'))
      return
    }
    const successful = await incidentStore.triggerSpecificIncident(props.incident.incidentId, {
      trigger: 'ended',
      eventTimestamp: endTimestamp.value.toMillis(),
    })
    if (!successful) {
      toast.error(t('toast.generic_could_not_save'))
      return
    }
    incidentId = props.incident.incidentId
  }
  toast.success(t('toast.generic_saved'))

  resetFields()
  emit('saved', incidentId)
}

function preFillTimestamps() {
  startTimestamp.value = null
  endTimestamp.value = null
  if (props.preFilledFirstTimestamp && !props.preFilledSecondTimestamp) {
    startTimestamp.value = DateTime.fromMillis(props.preFilledFirstTimestamp)
  }
  if (props.preFilledFirstTimestamp && props.preFilledSecondTimestamp) {
    startTimestamp.value = DateTime.fromMillis(Math.min(props.preFilledFirstTimestamp, props.preFilledSecondTimestamp))
    endTimestamp.value = DateTime.fromMillis(Math.max(props.preFilledFirstTimestamp, props.preFilledSecondTimestamp))
    hasAlreadyEnded.value = true
  }
}

onMounted(async () => {
  preFillTimestamps()
  schema.value = await incidentStore.getSimplifiedIncidentSchema('barnDocumentation')
  if (props.preFilledDescription) {
    description.value = props.preFilledDescription
  } else {
    description.value = null
  }
  if (props.preFilledDisplayIn) {
    if (!optionalOverrides.value) optionalOverrides.value = {}
    if (!optionalOverrides.value.displayConfiguration) {
      optionalOverrides.value.displayConfiguration = {}
    }
    optionalOverrides.value.displayConfiguration.includeIn = props.preFilledDisplayIn
  }
})
</script>

<template>
  <v-layout-card
    :loading="loading"
    :disabled="loading"
  >
    <v-row
      density="comfortable"
      class="justify-space-between"
    >
      <v-col cols="auto">
        <v-card-title>
          {{ incident ? $t('incidents.barnDocumentation.update_title') : $t('incidents.barnDocumentation.new_title') }}
        </v-card-title>
      </v-col>
      <v-col
        v-if="shrinkable"
        cols="auto"
      >
        <v-btn
          class="ma-2"
          :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          variant="tonal"
          color="primary"
          @click="expanded = !expanded"
        />
      </v-col>
    </v-row>

    <v-expand-transition mode="in-out">
      <v-card-text v-if="!shrinkable || expanded">
        <v-form ref="form">
          <div v-if="!incident">
            <h3 class="mt-2 mb-6">
              {{ $t('incidents.barnDocumentation.classification') }}
            </h3>
            <v-autocomplete
              v-model="classification"
              :items="availableClassifications"
              variant="outlined"
              :label="$t('incidents.barnDocumentation.classification')"
              :rules="[inputRequiredRule]"
            />
            <v-autocomplete
              v-model="specification"
              :items="availableSpecifications"
              variant="outlined"
              :label="$t('incidents.barnDocumentation.specification')"
              :disabled="classification === null"
              :rules="[inputRequiredRule]"
            />

            <div v-if="allowGlobal || allowCameraIds || allowRegions">
              <v-divider />
              <h3 class="my-2">
                {{ $t('incidents.barnDocumentation.location') }}
              </h3>
              <v-checkbox
                v-if="allowGlobal"
                v-model="isGlobal"
                :label="$t('incidents.barnDocumentation.is_global')"
              />

              <CameraPicker
                v-if="allowCameraIds"
                v-model="cameras"
                :disabled="isGlobal"
                :label="$t('general_interface.camera_picker.label')"
              />

              <RegionSpecificationPicker
                v-if="allowRegions"
                v-model="regionSpecifications"
                :disabled="isGlobal"
                :label="$t('general_interface.region_specification_picker.label')"
              />
            </div>

            <div>
              <v-divider />
              <h3 class="my-2">
                {{ $t('incidents.barnDocumentation.time') }}
              </h3>
              <DateTimePicker
                v-model="startTimestamp"
                fullwidth
                :label="useTimestamp ? $t('general_interface.date_time_picker.label') : $t('general_interface.date_time_picker.start')"
              />
              <v-row>
                <v-col v-if="hasAlreadyEnded">
                  <DateTimePicker
                    v-model="endTimestamp"
                    fullwidth
                    :disabled="useTimestamp"
                    :label="$t('general_interface.date_time_picker.end')"
                  />
                </v-col>
                <v-col cols="auto">
                  <v-switch
                    v-model="hasAlreadyEnded"
                    :label="$t('incidents.barnDocumentation.provideEndTimestampNow')"
                    :disabled="useTimestamp"
                    class="my-5"
                  />
                </v-col>
              </v-row>
            </div>

            <div>
              <v-divider />
              <h3 class="my-2">
                {{ $t('general_interface.display_settings') }}
              </h3>
              <IncidentOptionalOverrideForm
                v-model="optionalOverrides"
                :default-stakeholders="['contracting', 'production']"
              />
            </div>

            <div>
              <v-divider />
              <h3 class="my-2">
                {{ $t('incidents.barnDocumentation.additional') }}
              </h3>

              <WYSIWYG
                v-model="description"
                :label="$t('incidents.barnDocumentation.titles.description')"
              />
            </div>
          </div>
          <div v-if="incident && incident.state === 'created'">
            <DateTimePicker
              v-model="endTimestamp"
              :label="$t('general_interface.date_time_picker.end')"
            />
          </div>
        </v-form>
      </v-card-text>
    </v-expand-transition>

    <v-fade-transition mode="in-out">
      <v-card-actions
        v-if="!shrinkable || expanded"
        class="justify-end"
      >
        <v-btn
          v-if="!incident"
          color="error"
          variant="outlined"
          @click="resetFields"
        >
          {{ $t('general_interface.buttons.reset') }}
        </v-btn>

        <v-btn
          v-if="showCancelButton"
          variant="outlined"
          color="error"
          @click="$emit('cancel')"
        >
          {{ $t('general_interface.buttons.cancel') }}
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          @click="saveDocumentation"
        >
          {{ $t('general_interface.buttons.save') }}
        </v-btn>
      </v-card-actions>
    </v-fade-transition>
    <v-fade-transition mode="in-out">
      <v-card-actions
        v-if="shrinkable && !expanded"
        class="justify-center"
      >
        <v-btn
          variant="flat"
          color="success"
          class="rounded-pill"
          @click="expanded = true"
        >
          {{ $t('barn_documentation_list.add') }}
        </v-btn>
      </v-card-actions>
    </v-fade-transition>
  </v-layout-card>
</template>

<style scoped></style>
